/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';

/**
 * A single selector assignment rule mapping a condition value to a strategy ID.
 * Used by geo, content, and device selector types to look up the target
 * strategy for a given session attribute value.
 *
 * @example { condition: 'US', strategyId: 'us-high-yield' }
 * @example { condition: 'CTV', strategyId: 'ctv-premium' }
 */
export interface SelectorAssignmentRule {
  /** The condition value to match (e.g. country code, device type, content type) */
  condition: string;
  /** The ID of the request strategy to use when this condition matches */
  strategyId: string;
  /** Optional human-readable label for this rule */
  label?: string;
}

/**
 * Selector assignment configuration grouping rules by selector type.
 */
export interface SelectorAssignments {
  /** Rules for the geo-aware selector (condition = country/region/DMA) */
  geo?: SelectorAssignmentRule[];
  /** Rules for the content-aware selector (condition = contentType/playbackType) */
  content?: SelectorAssignmentRule[];
  /** Rules for the device-aware selector (condition = deviceType) */
  device?: SelectorAssignmentRule[];
}

export interface SystemDefaults {
  maxPodDuration: number;
  maxAdCount: number;
  minFillPercent: number;
  /** Seconds between ad breaks; a request strategy's `options.adBreakIntervalSeconds` overrides it (`#1053`). */
  adBreakIntervalSeconds: number;
  defaultReqStrategy?: string;
  defaultOptStrategy?: string;
  defaultDemandSource?: string;
  /** Fast-path (#794) overall wall-clock budget in ms for the immediate-response fast path. */
  fastPathOverallBudgetMs?: number;
  /** Fast-path (#794) per-demand-source timeout in ms. */
  fastPathPerSourceTimeoutMs?: number;
  /** Fast-path (#794) optional dedicated request strategy id/name (else the session strategy is used). */
  fastPathReqStrategyId?: string;
  /** Fast-path (#794) optional 0-based round index of the active strategy to use when no round is flagged fastPath. */
  fastPathRoundIndex?: number;
  /** Fast-path (#794) optional optimizer strategy id (else the pod/default optimizer is used). */
  fastPathOptimizerId?: string;
  /** Single-ad-break (#795) defer the req-loop/pod prefetch for auto (non-VMAP) fast-path sessions until a second ad-break confirms multi-break. Default true. */
  fastPathDeferReqLoopKickoff?: boolean;
  /** The strategy selector type to use (e.g. 'default', 'geo-aware', 'content-aware', 'device-aware', 'agentic') */
  strategySelectorId?: string;
  /** Assignment rules for each contextual selector type */
  selectorAssignments?: SelectorAssignments;
  cacheType?: 'file' | 'redis';
  redisUrl?: string;
  proxyPort?: number;
  adminPort?: number;
  reqLoopHttpPort?: number;
  reqLoopTcpPort?: number;
  optEngineHost?: string;
  optEngineTcpPort?: number;
  reqLoopHost?: string;
  metricsWorkerHost?: string;
  metricsWorkerHttpPort?: number;
  proxyBaseUrl?: string;
  proxyUrl?: string;
  /** Read-only runtime URLs surfaced from deployment env — not persisted to DB. */
  runtimeProxyUrl?: string;
  runtimeMockAdServerUrl?: string;
  runtimePrebidUrl?: string;
  /** Read-only: whether secret settings are encrypted when saved on this deployment (#1363). */
  runtimeSecretsEncrypted?: boolean;
  emailBackend?: 'console' | 'log' | 'sendgrid' | 'nodemailer';
  emailFrom?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser?: string;
  smtpPass?: string;
  sendgridApiKey?: string;
  adminVerificationBaseUrl?: string;
  adminConsoleUrl?: string;
  testAdPoolSize?: number;
  testAdDurationWeights?: {
    '<15': number;
    '15': number;
    '30': number;
    '45': number;
    '>45': number;
  };
  /** Share of the pod the test ad server fills, as percentages (#935). */
  testAdFillRange?: {
    min: number;
    max: number;
  };
  /** Share of test requests answered with zero ads — a no-bid (#935). */
  testAdNoFillRate?: number;
  testAdCpmRange?: {
    min: number;
    max: number;
  };
  configStorageType?: 'file' | 'sqlite' | 'postgres' | 'mysql';
  configDbHost?: string;
  configDbPort?: number;
  configDbName?: string;
  configDbUser?: string;
  configDbPassword?: string;
  metricsStorageEnabled?: boolean;
  metricsStorageType?: 'sqlite' | 'postgres' | 'mysql';
  metricsDbHost?: string;
  metricsDbPort?: number;
  metricsDbName?: string;
  metricsDbUser?: string;
  metricsDbPassword?: string;
}

export const configService = {
  async getSystemDefaults(): Promise<SystemDefaults> {
    const { data } = await api.get<SystemDefaults>('/api/system-defaults');
    return data;
  },

  async saveSystemDefaults(defaults: SystemDefaults): Promise<void> {
    await api.post('/api/system-defaults', defaults);
  },
};
